import { Head, Link, router } from '@inertiajs/react'
import { MoreHorizontal, Plus } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Pagination } from '@/components/Pagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { editUserPath, newUserPath, usersPath } from '@/lib/routes'
import type { PaginationMeta, UserRole } from '@/types/inertia'

type UserStatus = 'active' | 'pending' | 'disabled'

type UserRow = {
  id: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: UserRole
  status: UserStatus
  created_at: string
}

type UsersIndexProps = {
  users: UserRow[]
  pagination: PaginationMeta
}

const ROLE_LABEL: Record<UserRole, string> = {
  partner: 'Partner',
  lp: 'LP',
  founder: 'Founder',
}

const STATUS_VARIANT: Record<UserStatus, 'default' | 'secondary' | 'destructive' | 'accent'> = {
  active: 'default',
  pending: 'accent',
  disabled: 'destructive',
}

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'Active',
  pending: 'Pending invite',
  disabled: 'Disabled',
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function UsersIndex({ users, pagination }: UsersIndexProps) {
  return (
    <>
      <Head title="Users" />

      <div className="mx-auto max-w-6xl px-8 py-10">
        <PageHeader
          title="Users"
          description="Team members with access to this workspace."
          actions={
            <Link href={newUserPath()}>
              <Button>
                <Plus />
                New user
              </Button>
            </Link>
          }
        />

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-6 py-3 font-medium">Name</th>
                  <th className="px-6 py-3 font-medium">Email</th>
                  <th className="px-6 py-3 font-medium">Role</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Joined</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-10 text-center text-muted-foreground">
                      No users yet.
                    </td>
                  </tr>
                )}
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="cursor-pointer transition-colors hover:bg-muted/50"
                    onClick={() => router.visit(editUserPath(u.id))}
                  >
                    <td className="px-6 py-4 font-medium">{u.full_name || '—'}</td>
                    <td className="px-6 py-4 text-muted-foreground">{u.email}</td>
                    <td className="px-6 py-4">
                      <Badge variant="secondary">{ROLE_LABEL[u.role]}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={STATUS_VARIANT[u.status]}>{STATUS_LABEL[u.status]}</Badge>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button variant="ghost" size="icon" aria-label="Edit user" tabIndex={-1}>
                        <MoreHorizontal />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div className="mt-4">
          <Pagination
            basePath={usersPath()}
            currentPage={pagination.current_page}
            nextPage={pagination.next_page}
            prevPage={pagination.prev_page}
            totalPages={pagination.total_pages}
          />
        </div>
      </div>
    </>
  )
}

export default Object.assign(UsersIndex, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
